import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UserCog, X } from 'lucide-react'
import {
  listImpersonationTargets,
  setImpersonation,
  type ImpersonationTarget,
  type User,
} from '../api'

type Props = {
  /** The currently-effective user (request.user on the server). */
  user: User
  /** Called after a successful swap so the parent can re-fetch /me/. */
  onChange: () => void
}

/**
 * Top-bar dropdown: only rendered when the actor (real user) is a
 * superuser. Lets them act as a specific tenant user. The current
 * effective user is shown in the trigger; a banner-style chip is
 * shown when impersonating so it's never ambiguous.
 */
export default function ActAsPicker({ user, onChange }: Props) {
  const isSuperuser =
    user.is_superuser ||
    (user.acting_as?.real_is_superuser ?? false)
  const isActing = !!user.acting_as

  const [open, setOpen] = useState(false)
  const targets = useQuery({
    queryKey: ['impersonation-targets'],
    queryFn: listImpersonationTargets,
    enabled: isSuperuser,
    staleTime: 60_000,
  })
  const qc = useQueryClient()
  const swap = useMutation({
    mutationFn: (userId: number | null) => {
      // eslint-disable-next-line no-console
      console.info('[auth:act-as-swap]', { t: new Date().toISOString(), target_user_id: userId })
      return setImpersonation(userId)
    },
    onSuccess: () => {
      // eslint-disable-next-line no-console
      console.info('[auth:act-as-swap-success]', { t: new Date().toISOString() })
      setOpen(false)
      // Invalidate everything that depends on tenant scope.
      qc.invalidateQueries()
      onChange()
    },
  })

  // Close on Escape.
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open])

  if (!isSuperuser) return null

  // Group targets by client for the dropdown.
  const grouped: Record<string, ImpersonationTarget[]> = {}
  for (const t of targets.data || []) {
    const key = `${t.client_code} · ${t.client_name}`
    ;(grouped[key] ||= []).push(t)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        className={`me-btn ${isActing ? 'me-btn-primary' : 'me-btn-outline'} flex items-center gap-1.5`}
        title={
          isActing
            ? `Acting as ${user.username} (${user.client?.code ?? '—'}). Click to change or stop.`
            : 'Act as a tenant user'
        }
      >
        <UserCog size={12} />
        {isActing ? (
          <span className="me-mono text-[11px]">
            acting as {user.username}
          </span>
        ) : (
          <span className="me-mono text-[11px]">act as…</span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-1 max-h-[60vh] w-[320px] overflow-auto rounded-sm border me-border bg-[color:var(--me-surface)] shadow-lg"
        >
          <div className="flex items-center justify-between border-b me-border px-2 py-1.5">
            <span className="me-section-h !mb-0">Act as</span>
            <button
              onClick={() => setOpen(false)}
              className="me-btn me-btn-outline px-1 py-0"
              title="Close"
            >
              <X size={11} />
            </button>
          </div>

          {isActing && (
            <button
              onClick={() => swap.mutate(null)}
              disabled={swap.isPending}
              className="w-full border-b me-border px-2 py-1.5 text-left text-[12px] hover:bg-[color:var(--me-bg)]"
            >
              <span className="font-medium me-text">Stop acting as</span>
              <div className="me-mono text-[10px] me-text-mute">
                return to {user.acting_as?.real_username ?? '(self)'}
              </div>
            </button>
          )}

          {targets.isLoading && (
            <div className="px-2 py-2 text-[11px] me-text-mute">loading…</div>
          )}
          {!targets.isLoading && (targets.data || []).length === 0 && (
            <div className="px-2 py-2 text-[11px] me-text-mute">
              No tenant users available.
            </div>
          )}

          {Object.entries(grouped).map(([clientLabel, ts]) => (
            <div key={clientLabel}>
              <div className="me-section-h !mb-0 px-2 pb-0.5 pt-1.5">
                {clientLabel}
              </div>
              {ts.map((t) => {
                const isCurrent = t.user_id === user.id && isActing
                return (
                  <button
                    key={t.user_id}
                    onClick={() => swap.mutate(t.user_id)}
                    disabled={swap.isPending || isCurrent}
                    data-selected={isCurrent ? 'true' : undefined}
                    className="me-row !my-0 w-full"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium me-text">{t.username}</span>
                      <span className="me-mono text-[10px] me-text-mute">
                        {t.role}
                      </span>
                    </div>
                    {t.email && (
                      <div className="me-mono mt-0.5 truncate text-[10px] me-text-mute">
                        {t.email}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          ))}

          {swap.isError && (
            <div className="border-t border-[color:var(--me-warn)] bg-[color:var(--me-bg)] px-2 py-1.5 text-[11px] text-[color:var(--me-warn)]">
              {(swap.error as { response?: { data?: { detail?: string } }; message?: string })
                ?.response?.data?.detail ??
                (swap.error as { message?: string })?.message ??
                'Failed.'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
